import { createSelector } from 'reselect';
import type { RootState } from '../index';
import {
  buildGamesSearchKey,
  type GameDetailEntry,
  type GamesSearchPageEntry,
} from '../slices/games-slice';
import type { GamesSearchOrderBy } from '../../lib/api/games-api-client';
import { cappedAffiliateLinks } from '../../lib/helpers/capped-affiliate-links';

/**
 * Games slice selectors (task 6.6, public-website spec).
 *
 * Pure projections of `state.games` per design §3.1 (Selectors layer)
 * with two cap-aware shapings:
 *
 * - `selectGamesSearchPage` enforces the 20-items-per-page cap from
 *   parent `dvgup-platform` design §13 **Property 17** / R20.8 as a
 *   defensive client-side safeguard. The Smithy contract for
 *   `SearchGamesV1` already pins `items.length <= 20` server-side; this
 *   selector adds a client-side `slice(0, 20)` on `data.items` so a
 *   malformed payload from a misconfigured backend or test stub cannot
 *   render a 21+ tile grid downstream of the slice.
 * - `selectGameDetailWithAffiliates` enforces the 10-links-per-record
 *   cap from R12.3 / Property 17 by piping the cached
 *   `data.affiliateLinks` array through `cappedAffiliateLinks` (default
 *   cap = 10). The cap helper is the single canonical site for the
 *   affiliate-link cap per its file header; this selector is the
 *   single canonical site for projecting a `GameDetailEntry` into its
 *   render-ready, cap-applied shape.
 *
 * All three exports are memoized via `reselect.createSelector`.
 * Reselect v5's default `argsMemoize` and `memoize` are
 * `weakMapMemoize`, which uses a `WeakMap` for object arguments and a
 * regular `Map` for primitive arguments — sufficient for the
 * per-`gameId` and per-search-key memoization patterns these selectors
 * use without a manual factory layer.
 *
 * Function-length budget: every public export below stays under 20
 * lines per Requirement 3.7.
 */

/**
 * Defensive page-size cap applied to `selectGamesSearchPage`. Matches
 * the server-side cap in `SearchGamesV1` per Property 17 / R20.8.
 */
const SEARCH_PAGE_SIZE_CAP = 20;

/**
 * Input shape for {@link selectGamesSearchPage}. Mirrors the
 * `GetGamesSearchInput` shape from `games-api-client.ts` minus the
 * caller-side `q` requirement — selectors accept partial inputs so
 * the empty-`q` default-landing experience (R20.7) reuses the same
 * call shape as a real search.
 */
export interface SelectGamesSearchPageInput {
  readonly q?: string;
  readonly page?: number;
  readonly orderBy?: GamesSearchOrderBy;
}

/** Slice-level projector — the single source for both search selectors. */
const selectSearchPagesByKey = (
  state: RootState,
): Record<string, GamesSearchPageEntry> => state.games.searchPagesByKey;

/** Per-call projector resolving the input triple into its canonical key. */
const selectSearchKey = (
  _state: RootState,
  input: SelectGamesSearchPageInput,
): string =>
  buildGamesSearchKey({
    q: input.q ?? '',
    page: input.page,
    orderBy: input.orderBy,
  });

/**
 * Get the cached search-page entry for a `(q, page, orderBy)` triple,
 * with `data.items` defensively clamped to at most 20 entries per
 * Property 17 / R20.8.
 *
 * Empty-`q` triples are valid inputs — the Games landing's
 * default-landing experience (R20.7) shares this cache slot.
 *
 * Returns `undefined` when no entry is cached for the resolved key.
 * Returns the entry as-is (no projection) when `data` is absent (the
 * slice is in the loading or failed state).
 */
export const selectGamesSearchPage = createSelector(
  [selectSearchPagesByKey, selectSearchKey],
  (searchPagesByKey, key): GamesSearchPageEntry | undefined =>
    capSearchPageEntry(searchPagesByKey[key]),
);

/**
 * Get the cached search-page entry corresponding to the most-recent
 * search the user issued (`state.games.currentSearchKey`). Convenience
 * for `<GamesPage>` so the component does not re-derive the key on
 * render.
 *
 * Returns `undefined` when no search has been issued yet or when the
 * recorded key has been evicted from the cache.
 */
export const selectCurrentGamesSearchPage = createSelector(
  [
    selectSearchPagesByKey,
    (state: RootState) => state.games.currentSearchKey,
  ],
  (searchPagesByKey, currentKey): GamesSearchPageEntry | undefined =>
    currentKey === undefined
      ? undefined
      : capSearchPageEntry(searchPagesByKey[currentKey]),
);

/**
 * Defensive projection enforcing the per-page item cap from Property 17
 * / R20.8. Returns the entry unchanged when `data` is absent, when
 * `items` is missing or non-array (defense against an upstream
 * shape mismatch — early-bring-up runtime errors should degrade
 * gracefully rather than blow up the React tree), or when
 * `items.length` is already within the cap, preserving the input
 * reference so `createSelector`'s downstream memoization stays
 * referentially stable in the common case.
 */
function capSearchPageEntry(
  entry: GamesSearchPageEntry | undefined,
): GamesSearchPageEntry | undefined {
  if (entry === undefined) return undefined;
  const data = entry.data;
  if (data === undefined) return entry;
  if (!Array.isArray(data.items)) return entry;
  if (data.items.length <= SEARCH_PAGE_SIZE_CAP) return entry;
  return {
    ...entry,
    data: { ...data, items: data.items.slice(0, SEARCH_PAGE_SIZE_CAP) },
  };
}

/** Slice-level projector — the single source for the per-id detail selector. */
const selectGameDetailById = (
  state: RootState,
): Record<string, GameDetailEntry> => state.games.gameDetailById;

/** Per-call projector — passes the gameId through into the result function. */
const selectGameId = (_state: RootState, gameId: string): string => gameId;

/**
 * Get the cached `GameDetailEntry` for a game id with `data.affiliateLinks`
 * projected through {@link cappedAffiliateLinks} (default cap = 10 per
 * R12.3 / Property 17).
 *
 * Returns `undefined` when no entry is cached for the id. Returns the
 * entry as-is (no projection) when `data` is absent (the slice is in
 * the loading or failed state) — the entry still carries `status` and
 * `error` so the Game-page can render its loading / error chrome.
 *
 * Memoized per `(state, gameId)` via reselect's default
 * `weakMapMemoize`, so repeated calls with the same id return the
 * same projected entry reference. Different ids cache independently.
 */
export const selectGameDetailWithAffiliates = createSelector(
  [selectGameDetailById, selectGameId],
  (gameDetailById, gameId): GameDetailEntry | undefined => {
    const entry = gameDetailById[gameId];
    if (entry === undefined) return undefined;
    const data = entry.data;
    if (data === undefined) return entry;
    return {
      ...entry,
      data: { ...data, affiliateLinks: cappedAffiliateLinks(data.affiliateLinks) },
    };
  },
);
